import { useState, useMemo } from 'react';
import { Card, CardContent} from './card';
import { Button } from './button';
import { Download, Eye, EyeOff, BarChart3, Table, FileText, AlertCircle } from 'lucide-react';
import { DataTable } from './data-table';
import { ColumnDef } from '@tanstack/react-table';

interface DataVisualizationProps {
  data: any;
  title?: string;
  description?: string;
  type?: 'json' | 'csv' | 'text' | 'auto';
  className?: string;
  maxRows?: number;
  onDownload?: (data: any, format: string) => void;
}


export function DataVisualization({
  data,
  title = 'Data Output',
  description,
  className = '',
  maxRows = 100,
  onDownload
}: DataVisualizationProps) {
  const [viewMode, setViewMode] = useState<'table' | 'json' | 'raw'>('table');
  const [isExpanded, setIsExpanded] = useState(false);

  const processedData = useMemo(() => {
    if (!data) return null;

    try {
      // If data is a string, try to parse it
      let parsedData = data;
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch {
          // If JSON parsing fails, treat as raw text
          return { type: 'text', content: data };
        }
      }

      // Detect data type
      if (Array.isArray(parsedData)) {
        // Array of objects (typical CSV/table data)
        if (parsedData.length > 0 && typeof parsedData[0] === 'object') {
          const headers = Object.keys(parsedData[0]);
          const tableData = parsedData.slice(0, maxRows);
          return {
            type: 'table',
            headers,
            tableData,
            totalRows: parsedData.length,
            isLimited: parsedData.length > maxRows
          };
        }
        // Array of primitives
        return {
          type: 'list',
          items: parsedData.slice(0, maxRows),
          totalItems: parsedData.length,
          isLimited: parsedData.length > maxRows
        };
      }

      // Object (JSON data)
      if (typeof parsedData === 'object') {
        return { type: 'object', content: parsedData };
      }

      // Primitive value
      return { type: 'primitive', content: parsedData };
    } catch (error) {
      return { type: 'error', error: 'Failed to process data' };
    }
  }, [data, maxRows]);

  // Generate column definitions for DataTable
  const tableColumns = useMemo(() => {
    if (processedData?.type !== 'table' || !processedData.headers) return [];

    return processedData.headers.map((header): ColumnDef<any> => ({
      accessorKey: header,
      header: header,
      cell: ({ getValue }) => {
        const value = getValue();
        return formatValue(value);
      },
    }));
  }, [processedData]);

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const handleDownload = (format: string) => {
    if (onDownload) {
      onDownload(data, format);
    } else {
      // Default download behavior
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.toLowerCase().replace(/\s+/g, '_')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (!processedData) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-500">No data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (processedData.type === 'error') {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Processing Data
            </h3>
            <p className="text-sm text-gray-500">{processedData.error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className}>
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>{title}</span>
            </div>
            {description && <div>{description}</div>}
          </div>
          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className="flex border border-gray-200 rounded-lg">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 text-xs font-medium rounded-l-lg ${
                  viewMode === 'table'
                    ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                // disabled={processedData.type !== 'table'}
              >
                <Table className="h-3 w-3" />
              </button>
              <button
                onClick={() => setViewMode('json')}
                className={`px-3 py-1 text-xs font-medium ${
                  viewMode === 'json'
                    ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="h-3 w-3" />
              </button>
              {/* <button
                onClick={() => setViewMode('raw')}
                className={`px-3 py-1 text-xs font-medium rounded-r-lg ${
                  viewMode === 'raw'
                    ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Eye className="h-3 w-3" />
              </button> */}
            </div>

            {/* Download Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload('json')}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>

            {/* Expand/Collapse */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
      <div>
        {/* Table View */}
        {viewMode === 'table' && processedData.type === 'table' && processedData.headers && processedData.tableData && (
              <DataTable
                columns={tableColumns}
                data={processedData.tableData}
                searchPlaceholder="Search table..."
                searchColumn={processedData.headers[0]}
              />
        )}

        {/* JSON View */}
        {viewMode === 'json' && (
          <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto max-h-96">
            <code>{JSON.stringify(processedData.content || data, null, 2)}</code>
          </pre>
        )}

        {/* Raw View */}
        {viewMode === 'raw' && (
          <div className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto max-h-96">
            <pre>{typeof data === 'string' ? data : JSON.stringify(data, null, 2)}</pre>
          </div>
        )}

        {/* List View for arrays */}
        {processedData.type === 'list' && processedData.items && (
          <div className="space-y-2">
            <ul className="space-y-1">
              {processedData.items.slice(0, isExpanded ? undefined : 10).map((item, index) => (
                <li key={index} className="px-3 py-2 bg-gray-50 rounded text-sm">
                  {formatValue(item)}
                </li>
              ))}
            </ul>
            {processedData.isLimited && processedData.totalItems && (
              <p className="text-sm text-gray-500 text-center">
                Showing {Math.min(processedData.totalItems, maxRows)} of {processedData.totalItems} items
              </p>
            )}
          </div>
        )}

        {/* Object View */}
        {/* {processedData.type === 'object' && viewMode === 'table' && (
          <div className="space-y-2">
            {Object.entries(processedData.content).map(([key, value]) => (
              <div key={key} className="flex items-start space-x-3 py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">
                  {key}:
                </span>
                <span className="text-sm text-gray-900 break-all">
                  {formatValue(value)}
                </span>
              </div>
            ))}
          </div>
        )} */}

        {/* Primitive View */}
        {processedData.type === 'primitive' && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-900">{formatValue(processedData.content)}</span>
          </div>
        )}

        {/* Text View */}
        {processedData.type === 'text' && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <pre className="text-sm text-gray-900 whitespace-pre-wrap">{processedData.content}</pre>
          </div>
        )}
      </div>
    </div>
  );
}